/**
 * What a right-click on a message in a mail server folder offers.
 *
 * The inbox row's menu, translated into what a mailbox can do: read state is
 * the `\Seen` mark, the star is the `\Flagged` mark every mail client shows,
 * and archive, spam and delete are moves - into the folders the server itself
 * flags as Archive, Junk and Trash. An entry whose folder the mailbox does not
 * have is left out rather than offered and refused.
 *
 * DELETE MEANS TRASH, the way it does in every mail client. Only inside Trash,
 * or in a mailbox that has none, does the entry turn into "delete permanently",
 * red, behind a confirmation.
 *
 * Reply is deliberately absent. The message on screen is a copy of what the
 * server holds this second, not a CRM thread, and a reply from here would start
 * a conversation with no link back to it (see `ImapMessagePane`).
 */
import { Archive, Copy, Flag, FolderInput, Inbox, Mail, MailOpen, ShieldAlert, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { ImapMessageRow } from "../../../../lib/mail";
import type { ImapTargets } from "../../../../lib/mailActions";
import { useCopyToast } from "../../../ui";

/** Whether a delete here can only be permanent: inside Trash, or with no Trash. */
export function deleteIsPermanent(path: string, targets: ImapTargets | null): boolean {
  return !targets?.trash || targets.trash === path;
}

export function ImapMessageItems({
  row,
  path,
  targets,
  close,
  onOpen,
  onFlags,
  onMove,
  onPickFolder,
  onDelete,
}: {
  row: ImapMessageRow;
  /** The folder the message is in. */
  path: string;
  targets: ImapTargets | null;
  close: () => void;
  onOpen: (uid: number) => void;
  onFlags: (row: ImapMessageRow, change: { seen?: boolean; flagged?: boolean }) => void;
  /** Straight into a folder the menu already knows, like Archive. */
  onMove: (row: ImapMessageRow, to: string) => void;
  /** Ask which folder first. */
  onPickFolder: (row: ImapMessageRow) => void;
  onDelete: (row: ImapMessageRow) => void;
}) {
  const t = useT();
  const copy = useCopyToast();
  const who = row.fromName || row.fromAddress || t("email.imap.browse.noSender");
  const permanent = deleteIsPermanent(path, targets);
  const inJunk = targets?.junk === path;

  const act = (fn: () => void) => {
    close();
    fn();
  };

  return (
    <>
      <div className="crm-menu-head">
        <div className="crm-menu-t">{who}</div>
        <div className="crm-menu-s">{row.subject || t("email.imap.browse.noSubject")}</div>
      </div>

      <button type="button" className="crm-mi" onClick={() => act(() => onOpen(row.uid))}>
        <Mail className="size-4" />
        {t("email.imap.msg.open")}
      </button>

      {/* Unlike opening it here, which reads with BODY.PEEK and changes
          nothing, this entry DOES change the mark in every mail app the
          account is used from. That is the point of choosing it. */}
      <button type="button" className="crm-mi" onClick={() => act(() => onFlags(row, { seen: !row.seen }))}>
        {row.seen ? <Mail className="size-4" /> : <MailOpen className="size-4" />}
        {t(row.seen ? "email.row.markUnread" : "email.row.markRead")}
      </button>

      <button
        type="button"
        className={cn("crm-mi", row.flagged && "is-on")}
        onClick={() => act(() => onFlags(row, { flagged: !row.flagged }))}
      >
        <Flag className={cn("size-4", row.flagged && "fill-amber-400 text-amber-400")} />
        {t(row.flagged ? "email.imap.msg.unflag" : "email.imap.msg.flag")}
      </button>

      {targets?.archive && targets.archive !== path ? (
        <button type="button" className="crm-mi" onClick={() => act(() => onMove(row, targets.archive!))}>
          <Archive className="size-4" />
          {t("email.archive")}
        </button>
      ) : null}

      {/* In Junk the same slot takes the message back to the inbox, the way
          the thread menu's spam entry turns into "Not spam". */}
      {inJunk ? (
        targets?.inbox ? (
          <button type="button" className="crm-mi" onClick={() => act(() => onMove(row, targets.inbox!))}>
            <Inbox className="size-4" />
            {t("email.notSpam")}
          </button>
        ) : null
      ) : targets?.junk ? (
        <button type="button" className="crm-mi" onClick={() => act(() => onMove(row, targets.junk!))}>
          <ShieldAlert className="size-4" />
          {t("email.markSpam")}
        </button>
      ) : null}

      <button type="button" className="crm-mi" onClick={() => act(() => onPickFolder(row))}>
        <FolderInput className="size-4" />
        {t("email.imap.msg.move")}
      </button>

      {row.fromAddress ? (
        <button type="button" className="crm-mi" onClick={() => act(() => copy(row.fromAddress))}>
          <Copy className="size-4" />
          {t("email.row.copyAddress")}
        </button>
      ) : null}

      <div className="crm-menu-sep" />
      <button type="button" className={cn("crm-mi", permanent && "crm-mi-danger")} onClick={() => act(() => onDelete(row))}>
        <Trash2 className="size-4" />
        {t(permanent ? "email.imap.msg.deleteForever" : "email.imap.msg.delete")}
      </button>
    </>
  );
}
